/**
 * Document normalization — fills in what carries no information, and nothing else.
 *
 * The line this module walks matters. Fill too little and every hand-written
 * JSON fails validation over a missing `visible: true` nobody thinks to type.
 * Fill too much and the validator can never fail, the error list is always
 * empty, and the import screen becomes a preview of whatever garbage it was
 * handed.
 *
 * So: page ids, names, backgrounds and the boolean/geometry-neutral element
 * flags get defaults, because their absence means "the usual" and there is
 * exactly one usual. Content — element `id`, `type`, geometry, `text`,
 * `fontSize`, `fill`, `src` — is never invented. A missing element id is a real
 * authoring error, and the validator is supposed to say so.
 *
 * This module also strips executable content out of `svgContent`, which is the
 * one place an imported document can carry code rather than data. See
 * sanitizeSvgMarkup below for why that lives here.
 */

import { sanitizeHTML } from '../utils/sanitize';

/**
 * How many elements and attributes a parser sees in this markup.
 *
 * Comparing sanitizer input to output by string is useless: DOMPurify
 * re-serializes, turning `<rect/>` into `<rect></rect>`, so every clean
 * document would look modified and every import of legitimate SVG would carry
 * a spurious "unsafe markup was removed" warning. Counting what a parser
 * actually sees survives re-serialization and only moves when something was
 * genuinely taken out.
 */
const elementAndAttrCount = (markup) => {
  // An inert document: nothing in the markup loads or runs while it is counted.
  const host = window.document.implementation.createHTMLDocument('').createElement('div');
  host.innerHTML = markup;
  const nodes = host.querySelectorAll('*');
  let attrs = 0;
  nodes.forEach((node) => { attrs += node.attributes.length; });
  return `${nodes.length}:${attrs}`;
};

/**
 * Remove executable content from raw SVG markup.
 *
 * `svgContent` is the only field in an imported document that carries code
 * rather than data, and it reaches `dangerouslySetInnerHTML` in
 * PageThumbnailPreview — inline handlers on nodes inserted that way do fire.
 * Before the import screen existed, that markup came only from in-repo
 * template code. It is now pasted by an operator from whatever a language
 * model produced, and once the template is published it renders for every
 * end user. The app's CSP allows 'unsafe-inline', so it is no backstop.
 *
 * Delegates to `utils/sanitize.sanitizeHTML` — DOMPurify with the full SVG and
 * SVG-Filters profiles, already tuned in this repo for the exact vocabulary
 * Illustrator and Figma exports use, and already the sanitizer behind
 * ShapeThumbnail. Hand-rolled markup filtering was tried here first and was
 * wrong: a regex anchored on whitespace before an attribute name misses
 * `<svg/onload=…>`, because HTML also accepts `/` as an attribute separator,
 * and a scheme test against the raw attribute value misses
 * `href="jav&#x61;script:…"`, because the parser decodes entities afterwards.
 * Neither needs unusual browser behaviour. A real parser is not a nicety here.
 *
 * This runs during normalization rather than at the render sink because the
 * sink is shared with the editor, which the Global Constraints put off limits,
 * and because the engine is where "what will we accept from an author" is
 * already decided.
 *
 * @param {string} markup
 * @returns {{markup: string, changed: boolean}}
 */
export const sanitizeSvgMarkup = (markup) => {
  if (typeof markup !== 'string' || !markup) return { markup, changed: false };

  const clean = sanitizeHTML(markup);

  return { markup: clean, changed: elementAndAttrCount(clean) !== elementAndAttrCount(markup) };
};

/**
 * @param {Object} document
 * @returns {{document: Object, warnings: string[]}} the input is not mutated
 */
export const normalizeDocument = (document) => {
  const doc = document || {};
  const pages = Array.isArray(doc.pages) ? doc.pages : [];
  const warnings = [];

  const normalized = {
    ...doc,
    pages: pages.map((rawPage, i) => {
      const page = rawPage || {};
      const elements = Array.isArray(page.elements) ? page.elements : [];

      return {
        ...page,
        id: page.id || `page-${i + 1}`,
        name: page.name || `Page ${i + 1}`,
        background: typeof page.background === 'string' ? page.background : '#ffffff',
        elements: elements.map((rawEl) => {
          const el = rawEl || {};
          const out = {
            ...el,
            locked: typeof el.locked === 'boolean' ? el.locked : false,
            visible: typeof el.visible === 'boolean' ? el.visible : true,
            opacity: Number.isFinite(el.opacity) ? el.opacity : 1,
            rotation: Number.isFinite(el.rotation) ? el.rotation : 0,
            parentId: el.parentId === undefined ? null : el.parentId,
          };

          if (typeof el.svgContent === 'string') {
            const { markup, changed } = sanitizeSvgMarkup(el.svgContent);
            out.svgContent = markup;
            // Reported, never silent: an operator who pasted a template
            // carrying a script needs to know something was taken out of it.
            if (changed) {
              warnings.push(
                `${el.id || 'an svg element'}: unsafe markup was removed from svgContent`,
              );
            }
          }

          return out;
        }),
      };
    }),
  };

  return { document: normalized, warnings };
};
